import React, { useState, useRef, useEffect, useMemo } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useTranslations } from "@/hooks/use-translations";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/hooks/use-auth";
import { useActiveTeam } from "@/hooks/use-team";
import {
	api,
	type SeoAnalysis,
	type SeoAnalysisDetail,
	type SeoStatus,
	type SeoAiReport,
	type SeoCategoryKey,
	type SeoTechReport,
} from "@/lib/api";
import { toast } from "sonner";
import { Streamdown } from "streamdown";
import { useSearchParams } from "react-router-dom";
import { SeoFileReports } from "@/components/files/seo-file-reports";
import { SafeReportMarkdown } from "@/components/files/safe-report-markdown";
import { userFacingMessage } from "@/lib/user-facing-message";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
	ArrowLeft,
	Globe,
	Clock,
	Plus,
	Search,
	X,
	ChevronRight,
	Trash2,
	Sparkles,
	AlertTriangle,
	ChevronDown,
	Link2,
	MoreVertical,
	Pencil,
	Loader2,
	CheckCircle2,
	AlertCircle,
} from "lucide-react";
import { motion, AnimatePresence } from "motion/react";

/* ═══════════════════════════════════════════════════════════
   HELPERS
   ═══════════════════════════════════════════════════════════ */

// Backend status enum → the three UI states the badge understands.
function uiStatus(s: SeoStatus): "completed" | "processing" | "failed" | "needs-input" | "partial" {
	return s === "inProcess" ? "processing" : s === "needsInput" ? "needs-input" : s;
}

function extractDomain(rawUrl: string): string {
	try {
		let clean = rawUrl.trim();
		if (!clean.startsWith("http")) clean = "https://" + clean;
		return new URL(clean).hostname;
	} catch {
		return rawUrl;
	}
}

function pathOf(rawUrl: string): string {
	try {
		return new URL(rawUrl).pathname || "/";
	} catch {
		return rawUrl;
	}
}

function formatDate(dateStr: string) {
	return new Date(dateStr).toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" });
}

function prettyKey(key: string): string {
	return key
		.replace(/[_-]/g, " ")
		.replace(/([a-z])([A-Z])/g, "$1 $2")
		.replace(/\b\w/g, (c) => c.toUpperCase());
}

// The agent stores its JSON report as a string (sometimes fenced). Parse defensively.
function parseAiReport(raw: string | null): SeoAiReport | null {
	if (!raw) return null;
	try {
		let s = raw.trim();
		const fence = s.match(/```(?:json)?\s*([\s\S]*?)```/i);
		if (fence) s = fence[1].trim();
		return JSON.parse(s) as SeoAiReport;
	} catch {
		return null;
	}
}

// Display order + title for each category, and which raw techAnalysis fields back it.
const CATEGORY_META: {
	key: SeoCategoryKey;
	title: string;
	techKeys: (keyof SeoTechReport)[];
}[] = [
	{ key: "crawlability_indexability", title: "Crawlability & Indexability", techKeys: ["robotsTxt", "sitemap", "canonical"] },
	{ key: "on_page_seo", title: "On-Page SEO", techKeys: ["title", "metaDescription", "headings", "mediaAltText"] },
	{ key: "content_quality", title: "Content Quality", techKeys: [] },
	{ key: "technical_performance", title: "Technical SEO / Performance", techKeys: ["performance"] },
	{ key: "mobile_page_experience", title: "Mobile & Page Experience", techKeys: ["responsiveness"] },
	{ key: "https_security", title: "HTTPS / Security", techKeys: ["ssl"] },
	{ key: "internal_linking", title: "Internal Linking", techKeys: ["links"] },
	{ key: "international_seo", title: "International SEO (hreflang)", techKeys: ["hreflang", "multilingual"] },
	{ key: "structured_data", title: "Structured Data / Schema", techKeys: [] },
	{ key: "social_meta", title: "Social & Meta", techKeys: [] },
	{ key: "ai_search_geo", title: "AI Search / GEO", techKeys: [] },
];

const CATEGORY_TITLE: Record<SeoCategoryKey, string> = CATEGORY_META.reduce(
	(acc, c) => ({ ...acc, [c.key]: c.title }),
	{} as Record<SeoCategoryKey, string>,
);

/* ═══════════════════════════════════════════════════════════
   SHARED COMPONENTS
   ═══════════════════════════════════════════════════════════ */

function Breadcrumbs({ items }: { items: { label: string; onClick?: () => void }[] }) {
	return (
		<nav className="flex items-center gap-1.5 text-xs text-text-tertiary mb-6">
			{items.map((item, i) => (
				<React.Fragment key={i}>
					{i > 0 && <ChevronRight size={12} className="text-text-tertiary" />}
					{item.onClick ? (
						<button onClick={item.onClick} className="hover:text-text-primary transition-colors">
							{item.label}
						</button>
					) : (
						<span className="text-text-secondary">{item.label}</span>
					)}
				</React.Fragment>
			))}
		</nav>
	);
}

function StatusBadge({ status }: { status: "completed" | "processing" | "failed" | "needs-input" | "partial" }) {
	const cfg = ({
		completed: { tone: "success", label: "Completed" },
		processing: { tone: "info", label: "Processing" },
		failed: { tone: "danger", label: "Failed" },
		"needs-input": { tone: "info", label: "Needs input" },
		partial: { tone: "warning", label: "Partial" },
	} as const)[status];

	return (
		<Badge tone={cfg.tone} className="uppercase tracking-wider">
			{cfg.label}
		</Badge>
	);
}

// Recursive, readable renderer for raw techAnalysis values (no raw JSON dumps).
function DataView({ value }: { value: unknown }) {
	if (value === null || value === undefined) {
		return <span className="text-text-tertiary">—</span>;
	}
	if (typeof value === "boolean") {
		return <span className={value ? "text-success" : "text-danger"}>{value ? "yes" : "no"}</span>;
	}
	if (typeof value === "number" || typeof value === "string") {
		const str = String(value);
		return <span className="text-text-secondary break-words">{str.length === 0 ? "—" : str}</span>;
	}
	if (Array.isArray(value)) {
		if (value.length === 0) return <span className="text-text-tertiary">none</span>;
		return (
			<ul className="space-y-1">
				{value.slice(0, 50).map((v, i) => (
					<li key={i} className="pl-3 border-l border-border-subtle">
						<DataView value={v} />
					</li>
				))}
				{value.length > 50 && <li className="text-text-tertiary text-xs">+{value.length - 50} more…</li>}
			</ul>
		);
	}
	const entries = Object.entries(value as Record<string, unknown>);
	if (entries.length === 0) return <span className="text-text-tertiary">—</span>;
	return (
		<div className="space-y-1.5">
			{entries.map(([k, v]) => (
				<div key={k} className="grid grid-cols-[150px_1fr] gap-2 text-xs items-start">
					<span className="text-text-tertiary font-medium">{prettyKey(k)}</span>
					<DataView value={v} />
				</div>
			))}
		</div>
	);
}

/* ═══════════════════════════════════════════════════════════
   LEVEL 3 — URL ANALYSIS DETAIL PAGE
   ═══════════════════════════════════════════════════════════ */

function CategoryCard({
	categoryKey,
	assessment,
	tech,
}: {
	categoryKey: SeoCategoryKey;
	assessment: NonNullable<SeoAiReport["detailed_findings"][SeoCategoryKey]>;
	tech: SeoTechReport | null;
}) {
	const [expanded, setExpanded] = useState(false);
	const [showRaw, setShowRaw] = useState(false);

	const meta = CATEGORY_META.find((m) => m.key === categoryKey)!;
	const passed = assessment.status === "Passed";
	const hasRecommendation =
		assessment.recommendations && assessment.recommendations.trim().toLowerCase() !== "none";

	// Pick the mapped raw audit fields that actually have data.
	const rawData = useMemo(() => {
		if (!tech) return null;
		const picked: Record<string, unknown> = {};
		for (const k of meta.techKeys) {
			if (tech[k] !== undefined && tech[k] !== null) picked[k] = tech[k];
		}
		return Object.keys(picked).length > 0 ? picked : null;
	}, [tech, meta.techKeys]);

	return (
		<div>
			<button
				onClick={() => setExpanded(!expanded)}
				className="w-full flex items-center justify-between px-6 py-4 hover:bg-bg-sunken transition-all text-left"
			>
				<div className="flex items-center gap-3">
					{passed ? (
						<CheckCircle2 size={16} className="text-success flex-shrink-0" />
					) : (
						<AlertCircle size={16} className="text-danger flex-shrink-0" />
					)}
					<span className="text-sm font-semibold text-text-primary">{meta.title}</span>
					<Badge tone={passed ? "success" : "danger"}>{assessment.status}</Badge>
				</div>
				<motion.span animate={{ rotate: expanded ? 180 : 0 }} transition={{ duration: 0.15 }}>
					<ChevronDown size={18} className="text-text-tertiary" />
				</motion.span>
			</button>

			<AnimatePresence initial={false}>
				{expanded && (
					<motion.div
						initial={{ height: 0, opacity: 0 }}
						animate={{ height: "auto", opacity: 1 }}
						exit={{ height: 0, opacity: 0 }}
						transition={{ duration: 0.2 }}
						className="overflow-hidden bg-bg-sunken"
					>
						<div className="px-6 pb-6 pt-2 space-y-4">
							<div className="space-y-1">
								<span className="text-2xs font-semibold uppercase text-text-tertiary tracking-wider">Findings</span>
								<div className="chat-markdown text-xs leading-relaxed">
									<Streamdown>{assessment.findings}</Streamdown>
								</div>
							</div>

							{assessment.reference && (
								<div className="space-y-1">
									<span className="text-2xs font-semibold uppercase text-text-tertiary tracking-wider">Reference</span>
									<p className="text-xs text-text-tertiary leading-relaxed whitespace-pre-wrap">{assessment.reference}</p>
								</div>
							)}

							{hasRecommendation && (
								<div className="bg-bg-canvas border border-border-subtle rounded-xl p-3 space-y-1">
									<span className="text-2xs font-semibold uppercase text-text-primary tracking-wider">Recommendation</span>
									<div className="chat-markdown text-xs leading-relaxed">
										<Streamdown>{assessment.recommendations}</Streamdown>
									</div>
								</div>
							)}

							{rawData && (
								<div className="pt-1">
									<button
										onClick={() => setShowRaw(!showRaw)}
										className="flex items-center gap-1.5 text-2xs font-semibold text-text-tertiary hover:text-text-secondary transition-colors"
									>
										<ChevronDown size={13} className={`transition-transform ${showRaw ? "rotate-180" : ""}`} />
										Audit data
									</button>
									{showRaw && (
										<div className="mt-3 rounded-xl border border-border-subtle bg-bg-surface p-4">
											<DataView value={rawData} />
										</div>
									)}
								</div>
							)}
						</div>
					</motion.div>
				)}
			</AnimatePresence>
		</div>
	);
}

function UrlDetailPage({
	detail,
	domainName,
	onBack,
	onBackToList,
}: {
	detail: SeoAnalysisDetail;
	domainName: string;
	onBack: () => void;
	onBackToList: () => void;
}) {
	const report = useMemo(() => parseAiReport(detail.report), [detail.report]);
	const status = uiStatus(detail.status);

	const presentCategories = report
		? CATEGORY_META.filter((m) => report.detailed_findings[m.key])
		: [];

	return (
		<div className="min-h-screen p-6 bg-bg-canvas">
			<div className="max-w-7xl mx-auto space-y-8">
				<Breadcrumbs
					items={[
						{ label: "SEO Analyzer", onClick: onBackToList },
						{ label: domainName, onClick: onBack },
						{ label: pathOf(detail.analyzedUrl) },
					]}
				/>

				{/* Header */}
				<div className="flex flex-col md:flex-row md:items-start justify-between gap-4">
					<div className="flex items-start gap-4 min-w-0">
						<Button variant="secondary" size="icon-lg" onClick={onBack} className="flex-shrink-0 mt-1">
							<ArrowLeft size={20} />
						</Button>
						<div className="min-w-0">
							<h1 className="text-xl font-semibold text-text-primary truncate">{detail.analyzedUrl}</h1>
							<div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-text-tertiary mt-1">
								<span className="flex items-center gap-1">
									<Clock size={14} />
									Analyzed {formatDate(detail.createdAt)}
								</span>
								<StatusBadge status={status} />
							</div>
						</div>
					</div>
				</div>

				{/* Processing / failed / unparseable states */}
				{status === "processing" ? (
					<div className="flex flex-col items-center justify-center py-24 text-center">
						<Loader2 size={26} className="animate-spin text-text-tertiary mb-4" />
						<h2 className="text-lg font-semibold text-text-primary mb-2">Analysis in progress</h2>
						<p className="text-sm text-text-tertiary">Go back and reopen once it completes to see the report.</p>
					</div>
				) : status === "failed" ? (
					<div className="rounded-2xl border border-danger/20 bg-danger-bg p-6">
						<div className="flex items-center gap-2 mb-2">
							<AlertTriangle size={16} className="text-danger" />
							<h3 className="text-sm font-semibold text-danger">Analysis failed</h3>
						</div>
						<p className="text-xs text-text-secondary leading-relaxed">
							{typeof detail.error === "string"
								? detail.error
								: detail.error
									? JSON.stringify(detail.error)
									: "This URL could not be analyzed."}
						</p>
					</div>
				) : !report ? (
					<div className="rounded-2xl border border-border-subtle p-6 bg-bg-surface">
						<p className="text-sm text-text-secondary">
							The report for this URL could not be displayed.
							{detail.report && (
								<>
									<br />
									<span className="text-xs text-text-tertiary mt-3 block whitespace-pre-wrap">{detail.report}</span>
								</>
							)}
						</p>
					</div>
				) : (
					<>
						{/* Overall Assessment (replaces the old summary box) */}
						<div className="rounded-2xl border border-border-subtle p-6 bg-bg-surface">
							<div className="flex items-center gap-2 mb-3">
								<Sparkles size={16} className="text-text-primary" />
								<h3 className="text-xs font-semibold text-text-secondary tracking-wider uppercase">Overall Assessment</h3>
							</div>
							<div className="chat-markdown text-sm leading-relaxed font-light">
								<Streamdown>{report.overall_assessment}</Streamdown>
							</div>
						</div>

						{/* Detailed Findings by category */}
						{presentCategories.length > 0 && (
							<div className="rounded-2xl border border-border-subtle bg-bg-surface overflow-hidden">
								<div className="p-6 border-b border-border-subtle">
									<h3 className="text-text-primary font-semibold text-base">Detailed Findings</h3>
								</div>
								<div className="divide-y divide-border-subtle">
									{presentCategories.map((m) => (
										<CategoryCard
											key={m.key}
											categoryKey={m.key}
											assessment={report.detailed_findings[m.key]!}
											tech={detail.techAnalysis}
										/>
									))}
								</div>
							</div>
						)}

						{/* Priority Issues (optional) */}
						{report.priority_issues && report.priority_issues.length > 0 && (
							<div className="rounded-2xl border border-border-subtle p-6 bg-bg-surface space-y-4">
								<div className="flex items-center gap-2 pb-4 border-b border-border-subtle">
									<AlertTriangle size={18} className="text-text-primary" />
									<h3 className="text-text-primary font-semibold text-base">Priority Issues</h3>
								</div>
								<ol className="space-y-3">
									{report.priority_issues.map((issue, i) => (
										<li key={i} className="flex gap-3">
											<span className="flex-shrink-0 w-6 h-6 rounded-full bg-brand-100 text-text-primary text-xs font-bold flex items-center justify-center">
												{i + 1}
											</span>
											<div className="space-y-0.5">
												<span className="text-2xs font-bold uppercase tracking-wider text-text-tertiary">
													{CATEGORY_TITLE[issue.category] ?? prettyKey(issue.category)}
												</span>
												<p className="text-sm text-text-secondary leading-relaxed">{issue.issue}</p>
											</div>
										</li>
									))}
								</ol>
							</div>
						)}
					</>
				)}
			</div>
		</div>
	);
}

/* ═══════════════════════════════════════════════════════════
   LEVEL 2 — DOMAIN RESULTS PAGE
   ═══════════════════════════════════════════════════════════ */

function DomainResultsPage({
	analysis,
	onBack,
	onSelectUrl,
	t,
}: {
	analysis: SeoAnalysis;
	onBack: () => void;
	onSelectUrl: (detail: SeoAnalysisDetail) => void;
	t: ReturnType<typeof useTranslations>;
}) {
	const queryClient = useQueryClient();
	const [searchQuery, setSearchQuery] = useState("");
	const [page, setPage] = useState(0);
	const [answerText, setAnswerText] = useState("");

	const { data, isLoading } = useQuery({
		queryKey: ["seo-analysis", analysis.id, page],
		queryFn: () => api.getAnalysisDetails(analysis.id, page),
		refetchInterval: (query) => {
			const d = query.state.data;
			const processing =
				d?.analysis.status === "inProcess" || d?.details.some((x) => x.status === "inProcess");
			return processing ? 5000 : false;
		},
	});

	const details = data?.details ?? [];
	const currentAnalysis = data?.analysis ?? analysis;
	const hasMore = data?.hasMore ?? false;

	const { mutate: deleteUrl } = useMutation({
		mutationFn: (id: string) => api.deleteAnalysisUrl(id),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ["seo-analysis", analysis.id] });
			toast.success(t("deleted") || "Deleted");
		},
		onError: (err) => toast.error(err instanceof Error ? err.message : "Failed to delete"),
	});

	const { mutate: answerAnalysis, isPending: isAnswering } = useMutation({
		mutationFn: () => api.answerAnalysis(analysis.id, answerText),
		onSuccess: () => {
			setAnswerText("");
			queryClient.invalidateQueries({ queryKey: ["seo-analysis", analysis.id] });
			queryClient.invalidateQueries({ queryKey: ["seo-analyses"] });
		},
		onError: (err) => toast.error(err instanceof Error ? err.message : "Failed to continue audit"),
	});
	const { mutate: retryAnalysisResult, isPending: isRetryingResult } = useMutation({
		mutationFn: () => api.retryAnalysisResult(analysis.id),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ["seo-analysis", analysis.id] });
			queryClient.invalidateQueries({ queryKey: ["seo-analyses"] });
		},
		onError: (err) => toast.error(err instanceof Error ? err.message : "Failed to retry audit result"),
	});

	const filtered = useMemo(
		() => details.filter((u) => u.analyzedUrl.toLowerCase().includes(searchQuery.toLowerCase())),
		[details, searchQuery],
	);

	const counts = {
		completed: details.filter((u) => u.status === "completed").length,
		processing: details.filter((u) => u.status === "inProcess").length,
		failed: details.filter((u) => u.status === "failed").length,
	};

	const isProcessingEmpty = details.length === 0 && currentAnalysis.status === "inProcess";

	if (currentAnalysis.engine === "hermes") {
		const report = details[0]?.report;
		const error = currentAnalysis.error as { message?: string } | string | null;
		const errorMessage = typeof error === "string" ? error : error?.message;
		const canRetryResult = errorMessage === "Hermes did not return a validated audit report";
		const displayError = canRetryResult
			? t("auditInvalidReport")
			: errorMessage ? userFacingMessage(errorMessage) : t("auditFailed");
		return (
			<div className="min-h-screen p-6 bg-bg-canvas">
				<div className="max-w-5xl mx-auto space-y-6">
					<Breadcrumbs items={[{ label: "SEO Analyzer", onClick: onBack }, { label: currentAnalysis.name }]} />
					<div className="flex items-center gap-4">
						<Button variant="secondary" size="icon-lg" onClick={onBack} className="flex-shrink-0" aria-label="Back"><ArrowLeft size={20} /></Button>
						<div className="min-w-0">
							<h1 className="text-2xl font-semibold text-text-primary truncate">{currentAnalysis.name}</h1>
							<p className="text-sm text-text-tertiary truncate">{currentAnalysis.domain} · AI Search Audit</p>
						</div>
						<StatusBadge status={uiStatus(currentAnalysis.status)} />
					</div>
					{isLoading || currentAnalysis.status === "inProcess" ? (
						<div className="rounded-2xl border border-border-subtle bg-bg-surface p-10 text-center text-text-secondary">
							<Loader2 size={26} className="animate-spin mx-auto mb-4" />
							{t("auditRunning")}
						</div>
					) : currentAnalysis.status === "needsInput" ? (
						<div className="rounded-2xl border border-info/20 bg-bg-surface p-6 space-y-4">
							<h2 className="text-lg font-semibold text-text-primary">{t("auditNeedsClarification")}</h2>
							<p className="text-sm text-text-secondary whitespace-pre-wrap">{data?.question ? userFacingMessage(data.question) : t("auditClarifyScope")}</p>
							<form onSubmit={(event) => { event.preventDefault(); if (answerText.trim()) answerAnalysis(); }} className="space-y-3">
								<Textarea value={answerText} onChange={(event) => setAnswerText(event.target.value)} maxLength={2000}
									className="min-h-28 p-3" placeholder={t("auditAnswerPlaceholder")} />
								<Button type="submit" disabled={!answerText.trim() || isAnswering}>{t("auditContinue")}</Button>
							</form>
						</div>
					) : currentAnalysis.status === "failed" ? (
						<div className="rounded-2xl border border-danger/20 bg-danger-bg p-6 text-danger space-y-4">
							<p>{displayError}</p>
							{canRetryResult && <Button onClick={() => retryAnalysisResult()} disabled={isRetryingResult}>
								{t("auditRetryResult")}
							</Button>}
						</div>
					) : report ? (
						<div className="rounded-2xl border border-border-subtle bg-bg-surface p-6 md:p-10 chat-markdown text-sm leading-relaxed">
							<SafeReportMarkdown>{report}</SafeReportMarkdown>
						</div>
					) : (
						<SeoFileReports analysisId={currentAnalysis.id} processing={false} />
					)}
					{currentAnalysis.status === "inProcess" && <SeoFileReports analysisId={currentAnalysis.id} processing />}
				</div>
			</div>
		);
	}

	return (
		<div className="min-h-screen p-6 bg-bg-canvas">
			<div className="max-w-7xl mx-auto space-y-6">
				<Breadcrumbs
					items={[
						{ label: t("seoAnalyzerBreadcrumb") || "SEO Analyzer", onClick: onBack },
						{ label: analysis.domain },
					]}
				/>

				{/* Header */}
				<div className="flex items-start gap-4">
					<Button variant="secondary" size="icon-lg" onClick={onBack} className="flex-shrink-0 mt-1">
						<ArrowLeft size={20} />
					</Button>
					<div className="min-w-0">
						<h1 className="text-2xl font-semibold text-text-primary truncate">{analysis.name}</h1>
						<div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-text-tertiary mt-1">
							<a
								href={extractDomain(analysis.domain) ? `https://${extractDomain(analysis.domain)}` : analysis.domain}
								target="_blank"
								rel="noopener noreferrer"
								className="flex items-center gap-1 text-text-link underline underline-offset-2"
							>
								<Globe size={14} />
								{analysis.domain}
							</a>
							<span>•</span>
							<StatusBadge status={uiStatus(analysis.status)} />
						</div>
					</div>
				</div>

				{isLoading ? (
					<div className="flex items-center justify-center py-24">
						<Loader2 size={24} className="animate-spin text-text-tertiary" />
					</div>
				) : isProcessingEmpty ? (
					<div className="flex flex-col items-center justify-center py-24 text-center max-w-[420px] mx-auto">
						<Loader2 size={26} className="animate-spin text-text-tertiary mb-4" />
						<h2 className="text-2xl font-semibold text-text-primary mb-3">{t("processingTitle") || "Crawling your site"}</h2>
						<p className="text-sm text-text-tertiary leading-relaxed">
							{t("processingDescription") || "We're analyzing the pages on this domain. This can take a few minutes."}
						</p>
					</div>
				) : (
					<>
						{/* Per-page status counts */}
						<div className="grid grid-cols-3 gap-4">
							<div className="rounded-2xl border border-border-subtle p-4 bg-bg-surface">
								<span className="text-xs text-text-tertiary font-medium block">{t("completed") || "Completed"}</span>
								<span className="text-2xl font-bold text-success mt-2 block">{counts.completed}</span>
							</div>
							<div className="rounded-2xl border border-border-subtle p-4 bg-bg-surface">
								<span className="text-xs text-text-tertiary font-medium block">{t("processing") || "Processing"}</span>
								<span className="text-2xl font-bold text-info mt-2 block">{counts.processing}</span>
							</div>
							<div className="rounded-2xl border border-border-subtle p-4 bg-bg-surface">
								<span className="text-xs text-text-tertiary font-medium block">{t("failed") || "Failed"}</span>
								<span className="text-2xl font-bold text-danger mt-2 block">{counts.failed}</span>
							</div>
						</div>

						{/* Search */}
						<div className="relative">
							<span className="absolute inset-y-0 left-4 flex items-center text-text-tertiary">
								<Search size={18} />
							</span>
							<Input
								type="text"
								placeholder={t("filterUrls") || "Filter URLs…"}
								value={searchQuery}
								onChange={(e) => setSearchQuery(e.target.value)}
								className="h-11 pl-12 pr-4"
							/>
						</div>

						{/* URL list */}
						<div className="space-y-2">
							{filtered.length > 0 ? (
								filtered.map((u) => {
									const s = uiStatus(u.status);
									return (
										<div
											key={u.id}
											onClick={() => s === "completed" && onSelectUrl(u)}
											className={`group flex flex-col sm:flex-row sm:items-center justify-between gap-3 p-4 rounded-2xl border border-border-subtle bg-bg-surface transition-all ${
												s === "completed"
													? "hover:border-brand-700 hover:bg-brand-50 cursor-pointer hover:shadow-lg"
													: "opacity-70 cursor-default"
											}`}
										>
											<div className="flex items-center gap-3 min-w-0 flex-1">
												<Link2 size={14} className="text-text-tertiary flex-shrink-0" />
												<span className="text-sm text-text-primary truncate group-hover:text-text-primary transition-colors">
													{u.analyzedUrl}
												</span>
											</div>
											<div className="flex items-center gap-4 flex-shrink-0 text-xs text-text-tertiary">
												<span className="flex items-center gap-1">
													<Clock size={12} />
													{formatDate(u.createdAt)}
												</span>
												<StatusBadge status={s} />
												<Button
													variant="ghost"
													size="icon-sm"
													onClick={(e) => {
														e.stopPropagation();
														deleteUrl(u.id);
													}}
													className="text-text-tertiary hover:text-danger hover:bg-danger/10"
													title={t("delete") || "Delete"}
												>
													<Trash2 size={14} />
												</Button>
											</div>
										</div>
									);
								})
							) : (
								<div className="text-center py-16 text-text-tertiary text-sm">
									{t("noUrlsMatchFilter") || "No URLs match your filter."}
								</div>
							)}
						</div>

						{/* Pagination */}
						{(page > 0 || hasMore) && (
							<div className="flex items-center justify-center gap-3 pt-4">
								<Button
									variant="secondary"
									size="sm"
									onClick={() => setPage((p) => Math.max(0, p - 1))}
									disabled={page === 0}
								>
									{t("previous") || "Previous"}
								</Button>
								<span className="text-xs text-text-tertiary">{page + 1}</span>
								<Button
									variant="secondary"
									size="sm"
									onClick={() => setPage((p) => p + 1)}
									disabled={!hasMore}
								>
									{t("next") || "Next"}
								</Button>
							</div>
						)}
					</>
				)}
			</div>
		</div>
	);
}

/* ═══════════════════════════════════════════════════════════
   LEVEL 1 — DOMAIN LIST PAGE
   ═══════════════════════════════════════════════════════════ */

export function AnalyzerPage() {
	const [searchParams, setSearchParams] = useSearchParams();
	const t = useTranslations("analyzer");
	const { i18n } = useTranslation();
	const queryClient = useQueryClient();
	const { user } = useAuth();
	const { activeTeam } = useActiveTeam();
	const lang = (i18n.resolvedLanguage ?? i18n.language ?? "ru") as "en" | "ru";

	const [searchQuery, setSearchQuery] = useState("");
	const [modalOpen, setModalOpen] = useState(false);

	// Navigation state
	const [selectedAnalysis, setSelectedAnalysis] = useState<SeoAnalysis | null>(null);
	const [selectedDetail, setSelectedDetail] = useState<SeoAnalysisDetail | null>(null);

	// Create form state
	const [formName, setFormName] = useState("");
	const [formUrl, setFormUrl] = useState("");
	const [submitted, setSubmitted] = useState(false);
	const [errors, setErrors] = useState<{ name?: string; url?: string }>({});
	const nameRef = useRef<HTMLInputElement>(null);

	// Menu / rename / delete state
	const [activeMenuId, setActiveMenuId] = useState<string | null>(null);
	const [renameModalOpen, setRenameModalOpen] = useState(false);
	const [renameId, setRenameId] = useState<string | null>(null);
	const [renameName, setRenameName] = useState("");
	const [renameError, setRenameError] = useState("");
	const renameInputRef = useRef<HTMLInputElement>(null);
	const [deleteModalOpen, setDeleteModalOpen] = useState(false);
	const [deleteId, setDeleteId] = useState<string | null>(null);

	const { data: analyses = [], isLoading } = useQuery({
		queryKey: ["seo-analyses", user?.id, activeTeam?.id],
		queryFn: () => api.getAnalyses(activeTeam?.id),
		enabled: !!user?.id,
		refetchInterval: (query) => {
			const d = query.state.data as SeoAnalysis[] | undefined;
			return d?.some((a) => a.status === "inProcess") ? 5000 : false;
		},
	});
	const auditId = searchParams.get("audit");
	const directAudit = useQuery({
		queryKey: ["seo-direct-analysis", user?.id, auditId], queryFn: () => api.getAnalysisDetails(auditId!),
		enabled: !!auditId && !!user?.id,
	});
	useEffect(() => {
		if (directAudit.data && auditId) setSelectedAnalysis(directAudit.data.analysis);
	}, [auditId, directAudit.data]);

	const { mutate: createAnalysis, isPending: isCreating } = useMutation({
		mutationFn: () =>
			api.createAnalysis({
				name: formName.trim(),
				domain: formUrl.trim(),
				language: lang,
				...(activeTeam ? { teamId: activeTeam.id } : {}),
			}),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ["seo-analyses"] });
			setModalOpen(false);
			toast.success(t("analysisStarted") || "Analysis started");
		},
		onError: (err) => toast.error(err instanceof Error ? err.message : "Failed to start analysis"),
	});

	const { mutate: renameAnalysis, isPending: isRenaming } = useMutation({
		mutationFn: () => api.renameAnalysis(renameId!, renameName.trim()),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ["seo-analyses"] });
			setRenameModalOpen(false);
		},
		onError: (err) => toast.error(err instanceof Error ? err.message : "Failed to rename"),
	});

	const { mutate: deleteAnalysis } = useMutation({
		mutationFn: () => api.deleteAnalysis(deleteId!),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ["seo-analyses"] });
			setDeleteModalOpen(false);
			setDeleteId(null);
		},
		onError: (err) => toast.error(err instanceof Error ? err.message : "Failed to delete"),
	});

	useEffect(() => {
		if (modalOpen) {
			setTimeout(() => nameRef.current?.focus(), 100);
		} else {
			setFormName("");
			setFormUrl("");
			setErrors({});
			setSubmitted(false);
		}
	}, [modalOpen]);

	useEffect(() => {
		if (renameModalOpen) {
			setTimeout(() => renameInputRef.current?.focus(), 100);
		} else {
			setRenameName("");
			setRenameError("");
			setRenameId(null);
		}
	}, [renameModalOpen]);

	useEffect(() => {
		const handleClickOutside = () => setActiveMenuId(null);
		document.addEventListener("click", handleClickOutside);
		return () => document.removeEventListener("click", handleClickOutside);
	}, []);

	const handleCreate = (e: React.FormEvent) => {
		e.preventDefault();
		setSubmitted(true);
		const errs: { name?: string; url?: string } = {};
		if (!formName.trim()) errs.name = t("nameIsRequired") || "Name is required";
		if (!formUrl.trim()) errs.url = t("urlIsRequired") || "URL is required";
		setErrors(errs);
		if (Object.keys(errs).length > 0) return;
		createAnalysis();
	};

	const handleRename = (e: React.FormEvent) => {
		e.preventDefault();
		if (!renameName.trim()) {
			setRenameError(t("nameIsRequired") || "Name is required");
			return;
		}
		renameAnalysis();
	};

	const filtered = analyses.filter(
		(a) =>
			a.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
			a.domain.toLowerCase().includes(searchQuery.toLowerCase()),
	);

	/* ─── LEVEL 3: URL Detail ─── */
	if (selectedAnalysis && selectedDetail) {
		return (
			<UrlDetailPage
				detail={selectedDetail}
				domainName={selectedAnalysis.domain}
				onBack={() => setSelectedDetail(null)}
				onBackToList={() => {
					setSelectedDetail(null);
					setSelectedAnalysis(null);
				}}
			/>
		);
	}

	/* ─── LEVEL 2: Domain Results ─── */
	if (selectedAnalysis) {
		return (
			<DomainResultsPage
				analysis={selectedAnalysis}
				onBack={() => { setSelectedAnalysis(null); setSearchParams({}); }}
				onSelectUrl={(d) => setSelectedDetail(d)}
				t={t}
			/>
		);
	}

	/* ─── LEVEL 1: Domain List ─── */
	return (
		<div className="min-h-screen p-6 bg-bg-canvas">
			<div className="max-w-7xl mx-auto">
				{/* Header */}
				<div className="flex items-center justify-between mb-8">
					<div>
						<h1 className="text-2xl font-semibold text-text-primary">{t("title") || "SEO Analyzer"}</h1>
						<p className="text-sm text-text-tertiary mt-1">
							{t("description") || "Analyze how search engines and AI models perceive your website"}
						</p>
					</div>
					<Button onClick={() => setModalOpen(true)}>
						<Plus size={18} />
						{t("newAnalysis") || "New Analysis"}
					</Button>
				</div>

				{/* Search */}
				<div className="relative mb-6">
					<span className="absolute inset-y-0 left-4 flex items-center text-text-tertiary">
						<Search size={18} />
					</span>
					<Input
						type="text"
						placeholder={t("searchByNameOrDomain") || "Search by name or domain…"}
						value={searchQuery}
						onChange={(e) => setSearchQuery(e.target.value)}
						className="h-11 pl-12 pr-4"
					/>
				</div>

				{/* List */}
				<div className="space-y-3">
					<button
						onClick={() => setModalOpen(true)}
						className="w-full flex items-center justify-center gap-2 p-5 rounded-2xl border-2 border-dashed border-border-subtle text-text-tertiary hover:text-text-secondary hover:border-border-control hover:bg-bg-canvas transition-all text-sm font-semibold"
					>
						<Plus size={18} />
						{t("createNewAnalysis") || "Create new analysis"}
					</button>

					{isLoading ? (
						<div className="flex items-center justify-center py-16">
							<Loader2 size={24} className="animate-spin text-text-tertiary" />
						</div>
					) : filtered.length > 0 ? (
						filtered.map((a) => (
							<div
								key={a.id}
								onClick={() => setSelectedAnalysis(a)}
								className="group w-full flex flex-col sm:flex-row sm:items-center justify-between gap-4 p-5 rounded-2xl border border-border-subtle bg-bg-surface hover:border-brand-700 hover:bg-brand-50 cursor-pointer shadow-md hover:shadow-lg transition-all"
							>
								<div className="min-w-0 space-y-1">
									<h3 className="text-sm font-semibold text-text-primary group-hover:text-text-primary transition-colors truncate">
										{a.name}
									</h3>
									<div className="flex items-center gap-2 text-xs text-text-tertiary">
										<Globe size={14} className="text-text-tertiary flex-shrink-0" />
										<span className="truncate">{a.domain}</span>
									</div>
								</div>

								<div className="flex items-center justify-between sm:justify-end gap-4 sm:gap-6 text-xs text-text-tertiary flex-shrink-0">
									<div className="flex items-center gap-1.5">
										<Clock size={12} />
										<span>{formatDate(a.createdAt)}</span>
									</div>

									<StatusBadge status={uiStatus(a.status)} />

									<div className="relative">
										<Button
											variant="ghost"
											size="icon-sm"
											onClick={(e) => {
												e.stopPropagation();
												setActiveMenuId(activeMenuId === a.id ? null : a.id);
											}}
											className="text-text-tertiary relative z-10"
											title={t("actions") || "Actions"}
										>
											<MoreVertical size={16} />
										</Button>
										<AnimatePresence>
											{activeMenuId === a.id && (
												<motion.div
													initial={{ opacity: 0, scale: 0.95, y: -5 }}
													animate={{ opacity: 1, scale: 1, y: 0 }}
													exit={{ opacity: 0, scale: 0.95, y: -5 }}
													transition={{ duration: 0.15 }}
													onClick={(e) => e.stopPropagation()}
													className="absolute right-0 mt-1 w-36 rounded-xl border border-border-subtle bg-bg-surface p-1.5 shadow-popover z-20"
												>
													<button
														onClick={(e) => {
															e.stopPropagation();
															setRenameId(a.id);
															setRenameName(a.name);
															setRenameModalOpen(true);
															setActiveMenuId(null);
														}}
														className="w-full flex items-center px-3 py-2 rounded-xl text-xs text-text-secondary hover:text-text-primary hover:bg-bg-canvas transition-colors"
													>
														{t("rename") || "Rename"}
													</button>
													<button
														onClick={(e) => {
															e.stopPropagation();
															setDeleteId(a.id);
															setDeleteModalOpen(true);
															setActiveMenuId(null);
														}}
														className="w-full flex items-center px-3 py-2 rounded-xl text-xs text-danger hover:bg-danger/10 transition-colors"
													>
														{t("delete") || "Delete"}
													</button>
												</motion.div>
											)}
										</AnimatePresence>
									</div>
								</div>
							</div>
						))
					) : (
						<div className="text-center py-16 text-text-tertiary text-sm">{t("noAnalysesFound") || "No analyses yet."}</div>
					)}
				</div>
			</div>

			{/* ─── New Analysis Modal ─── */}
			<AnimatePresence>
				{modalOpen && (
					<div
						className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-scrim/30 backdrop-blur-sm"
						onClick={() => setModalOpen(false)}
					>
						<motion.div
							initial={{ opacity: 0, scale: 0.95, y: 15 }}
							animate={{ opacity: 1, scale: 1, y: 0 }}
							exit={{ opacity: 0, scale: 0.95, y: 15 }}
							transition={{ duration: 0.2, ease: "easeOut" }}
							onClick={(e) => e.stopPropagation()}
							className="w-full max-w-md rounded-2xl border border-border-subtle bg-bg-surface overflow-hidden shadow-popover"
						>
							<div className="flex items-center justify-between px-6 py-4 border-b border-border-subtle">
								<h2 className="text-base font-bold text-text-primary">{t("startNewAnalysis") || "Start new analysis"}</h2>
								<Button variant="ghost" size="icon-sm" onClick={() => setModalOpen(false)}>
									<X size={20} />
								</Button>
							</div>
							<form onSubmit={handleCreate}>
								<div className="p-6 space-y-4">
									<Input
										ref={nameRef}
										type="text"
										label={t("name") || "Name"}
										error={submitted ? errors.name : undefined}
										placeholder="e.g. SEO Audit - Homepage"
										value={formName}
										disabled={isCreating}
										onChange={(e) => {
											setFormName(e.target.value);
											if (submitted) setErrors((p) => ({ ...p, name: undefined }));
										}}
									/>
									<Input
										type="text"
										label={t("url") || "URL"}
										error={submitted ? errors.url : undefined}
										placeholder="e.g. example.com"
										value={formUrl}
										disabled={isCreating}
										onChange={(e) => {
											setFormUrl(e.target.value);
											if (submitted) setErrors((p) => ({ ...p, url: undefined }));
										}}
									/>
									<p className="text-xs text-text-tertiary">{t("auditType")}</p>
								</div>
								<div className="flex justify-end gap-3 px-6 py-4 border-t border-border-subtle">
									<Button
										type="button"
										variant="secondary"
										size="sm"
										onClick={() => setModalOpen(false)}
									>
										{t("cancel") || "Cancel"}
									</Button>
									<Button
										type="submit"
										size="sm"
										disabled={isCreating}
									>
										{isCreating && <Loader2 size={14} className="animate-spin" />}
										{t("create") || "Create"}
									</Button>
								</div>
							</form>
						</motion.div>
					</div>
				)}
			</AnimatePresence>

			{/* ─── Rename Modal ─── */}
			<AnimatePresence>
				{renameModalOpen && (
					<div
						className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-scrim/30 backdrop-blur-sm"
						onClick={() => setRenameModalOpen(false)}
					>
						<motion.div
							initial={{ opacity: 0, scale: 0.95, y: 15 }}
							animate={{ opacity: 1, scale: 1, y: 0 }}
							exit={{ opacity: 0, scale: 0.95, y: 15 }}
							transition={{ duration: 0.2, ease: "easeOut" }}
							onClick={(e) => e.stopPropagation()}
							className="w-full max-w-md rounded-2xl border border-border-subtle bg-bg-surface overflow-hidden shadow-popover"
						>
							<div className="flex items-center justify-between px-6 py-4 border-b border-border-subtle">
								<h2 className="text-base font-bold text-text-primary">{t("renameAnalysis") || "Rename analysis"}</h2>
								<Button variant="ghost" size="icon-sm" onClick={() => setRenameModalOpen(false)}>
									<X size={20} />
								</Button>
							</div>
							<form onSubmit={handleRename}>
								<div className="p-6 space-y-4">
									<Input
										ref={renameInputRef}
										type="text"
										label={t("name") || "Name"}
										error={renameError || undefined}
										placeholder="e.g. SEO Audit - Homepage"
										value={renameName}
										disabled={isRenaming}
										onChange={(e) => {
											setRenameName(e.target.value);
											if (renameError) setRenameError("");
										}}
									/>
								</div>
								<div className="flex justify-end gap-3 px-6 py-4 border-t border-border-subtle">
									<Button
										type="button"
										variant="secondary"
										size="sm"
										onClick={() => setRenameModalOpen(false)}
									>
										{t("cancel") || "Cancel"}
									</Button>
									<Button
										type="submit"
										size="sm"
										disabled={isRenaming}
									>
										{isRenaming && <Loader2 size={14} className="animate-spin" />}
										{t("saveChanges") || "Save changes"}
									</Button>
								</div>
							</form>
						</motion.div>
					</div>
				)}
			</AnimatePresence>

			{/* ─── Delete Confirmation Modal ─── */}
			<AnimatePresence>
				{deleteModalOpen && (
					<div
						className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-scrim/30 backdrop-blur-sm"
						onClick={() => setDeleteModalOpen(false)}
					>
						<motion.div
							initial={{ opacity: 0, scale: 0.95, y: 15 }}
							animate={{ opacity: 1, scale: 1, y: 0 }}
							exit={{ opacity: 0, scale: 0.95, y: 15 }}
							transition={{ duration: 0.2, ease: "easeOut" }}
							onClick={(e) => e.stopPropagation()}
							className="w-full max-w-md rounded-2xl border border-border-subtle bg-bg-surface overflow-hidden shadow-popover"
						>
							<div className="flex items-center justify-between px-6 py-4 border-b border-border-subtle">
								<h2 className="text-base font-bold text-text-primary">{t("deleteAnalysis") || "Delete analysis"}</h2>
								<Button variant="ghost" size="icon-sm" onClick={() => setDeleteModalOpen(false)}>
									<X size={20} />
								</Button>
							</div>
							<div className="p-6 space-y-3">
								<p className="text-sm text-text-secondary">
									{t("deleteConfirmation") || "Are you sure you want to delete this analysis? This cannot be undone."}
								</p>
							</div>
							<div className="flex justify-end gap-3 px-6 py-4 border-t border-border-subtle">
								<Button
									type="button"
									variant="secondary"
									size="sm"
									onClick={() => setDeleteModalOpen(false)}
								>
									{t("cancel") || "Cancel"}
								</Button>
								<Button
									variant="danger"
									size="sm"
									onClick={() => deleteAnalysis()}
								>
									{t("delete") || "Delete"}
								</Button>
							</div>
						</motion.div>
					</div>
				)}
			</AnimatePresence>
		</div>
	);
}
